import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { analyticsService } from '../services/analyticsService';
import SnakeGame from '../components/SnakeGame';
import TicTacToeGame from '../components/TicTacToeGame';
import './HomePage.css';
import './HomePage.modern.css';
import './HomePage.features.css';

const HERO_STEPS = [
  {
    icon: '🧭',
    title: 'Pick a Quest',
    description: 'Browse live skill-building adventures with real coaches.',
  },
  {
    icon: '🎮',
    title: 'Play & Build',
    description: 'Create games, art bots, and stories while you learn.',
  },
  {
    icon: '🏆',
    title: 'Earn Badges',
    description: 'Collect rewards as you level up every new skill.',
  },
];

const JOURNEY_CARDS = [
  {
    icon: '🎯',
    title: 'Daily Missions',
    copy: 'Kids unlock mini-challenges curated by coaches.',
    accent: 'mint',
  },
  {
    icon: '🤝',
    title: 'Coach Shout-outs',
    copy: 'Instant feedback, high-fives, and pep talks in chat.',
    accent: 'berry',
  },
  {
    icon: '🛡️',
    title: 'Safe Space',
    copy: 'Guardrails, classroom modes, and parent dashboards.',
    accent: 'sunrise',
  },
];

const NEW_FEATURES = [
  {
    icon: '🎨',
    title: 'Visual Code Canvas',
    description: 'Create stunning art with turtle graphics and code',
    link: '/code-canvas',
    color: '#9b59b6',
    badge: 'NEW'
  },
  {
    icon: '🎬',
    title: 'Code TikTok',
    description: 'Swipe through bite-sized coding challenges',
    link: '/code-tiktok',
    color: '#e74c3c',
    badge: 'HOT'
  },
  {
    icon: '🏪',
    title: 'Course Marketplace',
    description: 'Learn from expert instructors worldwide',
    link: '/marketplace',
    color: '#3498db',
    badge: 'NEW'
  },
  {
    icon: '🌍',
    title: 'Metaverse Classroom',
    description: '3D virtual campus with AI-powered learning',
    link: '/metaverse',
    color: '#16a085',
    badge: 'NEW',
    requiresAuth: true
  }
];

const BADGES = [
  { icon: '💡', label: 'Creative Thinker' },
  { icon: '⚡', label: 'Quick Coder' },
  { icon: '🛡️', label: 'Cyber Hero' },
];

const AVATAR_OPTIONS = ['🦄', '🦊', '🐯', '🐼', '🚀'];

const FLOATING_CHARMS = ['⭐', '🎈', '🧩', '🌟', '🚀', '🎨'];

const MINI_GAME_TABS = [
  { id: 'snake', icon: '🐍', label: 'Snake Dash', blurb: 'Boost focus as you dodge neon obstacles.' },
  { id: 'ticTacToe', icon: '⭕', label: 'Tic Tac Glow', blurb: 'Challenge a buddy to a rapid duel.' },
];

const HomePage = () => {
  const [showSignUp, setShowSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(AVATAR_OPTIONS[0]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [activeGame, setActiveGame] = useState('snake');
  const { signUp, signIn, resetPassword, user, loading: authLoading } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top when landing on home (e.g. from "Sign in" / "Sign up" on sessions page)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Track page view
  useEffect(() => {
    analyticsService.trackPageView('home');
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      if (user.type === 'admin') {
        navigate('/admin');
      } else if (user.type === 'coach') {
        navigate('/coach');
      } else if (user.type === 'parent') {
        navigate('/parent');
      } else {
        navigate('/student');
      }
    } else if (!authLoading && !user) {
      // User is signed out, ensure we're on home page
      if (location.pathname !== '/') {
        navigate('/');
      }
    }
  }, [user, authLoading, navigate, location.pathname]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      // Navigation will happen automatically via useEffect
    } catch (error) {
      setError(getErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      // All signups are students - admin users must be created in Firestore
      await signUp(email, password, name, 'student');
      // Navigation will happen automatically via useEffect
    } catch (error) {
      // Extract error code from Firebase error object
      const errorCode = error?.code || error?.message || 'unknown-error';
      setError(getErrorMessage(errorCode));
      // If email already in use, suggest signing in instead
      if (errorCode === 'auth/email-already-in-use') {
        // Optionally switch to sign-in form
        setTimeout(() => {
          setShowSignUp(false);
          setEmail(email); // Keep the email filled in
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };


  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setResetSuccess(false);
    
    if (!resetEmail.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    setResetLoading(true);
    try {
      await resetPassword(resetEmail);
      setResetSuccess(true);
      setError('');
    } catch (error) {
      const errorCode = error?.code || error?.message || 'unknown-error';
      if (errorCode === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (errorCode === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else {
        setError(`Error: ${errorCode}. Please try again.`);
      }
      setResetSuccess(false);
    } finally {
      setResetLoading(false);
    }
  };

  const getErrorMessage = (errorCode) => {
    // Check for CAPTCHA-related errors
    if (errorCode && errorCode.toString().includes('CAPTCHA')) {
      return 'Security verification failed. Please try again.';
    }
    if (errorCode && errorCode.toString().includes('Rate limit')) {
      return 'Too many attempts. Please wait a moment and try again.';
    }
    if (errorCode && errorCode.toString().includes('Suspicious')) {
      return 'Unusual activity detected. Please contact support if this persists.';
    }
    
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'This email is already registered. Please sign in instead.';
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/weak-password':
        return 'Password is too weak. Please use a stronger password.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/invalid-credential':
        return 'Invalid email or password.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in popup was closed. Please try again.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection and try again.';
      default:
        return `An error occurred: ${errorCode || 'Unknown error'}. Please try again.`;
    }
  };

  const activeGameConfig = MINI_GAME_TABS.find((tab) => tab.id === activeGame);

  const handleAuthModeChange = (mode) => {
    setShowSignUp(mode === 'signup');
    setError('');
  };

  const handleAvatarSelect = (avatar) => {
    setSelectedAvatar(avatar);
  };

  if (authLoading) {
    return (
      <div className="home-page">
        <div className="home-container" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh' 
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <p style={{ color: '#6b7280', fontSize: '1rem' }}>Loading...</p>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="home-page">
      <div className="floating-charms" aria-hidden="true">
        {FLOATING_CHARMS.map((charm, index) => (
          <span key={`${charm}-${index}`} className={`charm charm-${(index % 6) + 1}`}>
            {charm}
          </span>
        ))}
      </div>

      <section className="home-hero">
        <div className="hero-left">
          <div className="hero-pill">New! Winter Wonder Quests are live ❄️</div>
          <h1 className="hero-title">Where kids turn their imagination into real skills</h1>
          <p className="hero-lead">
            Live, coach-led skill-building adventures that mix technology, creativity, and interactive learning so every learner feels like the main character.
          </p>
          <div className="hero-steps">
            {HERO_STEPS.map((step) => (
              <div key={step.title} className="hero-step">
                <span className="step-icon" aria-hidden="true">{step.icon}</span>
                <div>
                  <p className="step-title">{step.title}</p>
                  <p className="step-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="hero-ctas">
            <Link to="/sessions" className="cta primary">
              📅 Explore Sessions
            </Link>
            <button type="button" className="cta ghost" onClick={() => handleAuthModeChange('signup')}>
              ✨ Create Account
            </button>
          </div>
        </div>

        <div className="hero-right">
          <div className="auth-card">
            <div className="auth-toggle">
              <button
                type="button"
                className={!showSignUp ? 'active' : ''}
                onClick={() => handleAuthModeChange('signin')}
              >
                Sign In
              </button>
              <button
                type="button"
                className={showSignUp ? 'active' : ''}
                onClick={() => handleAuthModeChange('signup')}
              >
                Create Account
              </button>
            </div>

            {error && (
              <div className="auth-alert" role="alert">
                {error}
              </div>
            )}

            {!showSignUp ? (
              <form className="auth-form" onSubmit={handleSignIn}>
                <label className="form-field">
                  <span>Email</span>
                  <input
                    type="email"
                    placeholder="kiddo@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </label>
                <label className="form-field">
                  <span>Password</span>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </label>
                <div className="form-meta">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setResetEmail(email);
                      setError('');
                    }}
                  >
                    Forgot password?
                  </button>
                </div>
                <button type="submit" className="cta primary w-100" disabled={loading}>
                  {loading ? 'Signing you in...' : 'Sign In'}
                </button>
                <div className="captcha-protected">
                  <span className="captcha-protected-icon">🔒</span>
                  <span>Protected by reCAPTCHA</span>
                </div>
              </form>
            ) : (
              <form className="auth-form" onSubmit={handleSignUp}>
                <div className="avatar-picker">
                  <p>Choose your starter avatar</p>
                  <div className="avatar-options">
                    {AVATAR_OPTIONS.map((avatar) => (
                      <button
                        type="button"
                        key={avatar}
                        className={`avatar-chip ${selectedAvatar === avatar ? 'selected' : ''}`}
                        onClick={() => handleAvatarSelect(avatar)}
                        aria-label={`Select avatar ${avatar}`}
                      >
                        {avatar}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="form-field">
                  <span>Full Name</span>
                  <input
                    type="text"
                    placeholder="Jamie Stellar"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </label>
                <label className="form-field">
                  <span>Email</span>
                  <input
                    type="email"
                    placeholder="parent@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </label>
                <label className="form-field">
                  <span>Password</span>
                  <input
                    type="password"
                    placeholder="Min 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </label>
                <button type="submit" className="cta primary w-100" disabled={loading}>
                  {loading ? 'Creating magic...' : 'Create Account'}
                </button>
                <div className="captcha-protected">
                  <span className="captcha-protected-icon">🔒</span>
                  <span>Protected by reCAPTCHA</span>
                </div>
                <button
                  type="button"
                  className="cta ghost w-100"
                  onClick={() => {
                    handleAuthModeChange('signin');
                    setName('');
                    setPassword('');
                  }}
                >
                  Back to sign in
                </button>
              </form>
            )}

            <p className="auth-footnote">All new accounts start as students. Parents can upgrade later.</p>
          </div>
        </div>
      </section>

      {/* Content below login disabled */}
      {false && (
      <>
      <section className="journey-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">After the doorway</p>
            <h2>Learning journeys stay playful & safe</h2>
          </div>
          <span className="section-chip">Kid & coach approved</span>
        </div>
        <div className="journey-grid">
          {JOURNEY_CARDS.map((card) => (
            <article key={card.title} className={`journey-card accent-${card.accent}`}>
              <span className="card-icon" aria-hidden="true">{card.icon}</span>
              <h3>{card.title}</h3>
              <p>{card.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="badges-section">
        <div className="badges-panel">
          <div>
            <p className="eyebrow">Gamified growth</p>
            <h3>Earn badges & cheers from your coach</h3>
          </div>
          <div className="badge-row">
            {BADGES.map((badge) => (
              <div key={badge.label} className="badge-chip">
                <span>{badge.icon}</span>
                <p>{badge.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="new-features-section">
        <div className="section-header">
          <p className="eyebrow">✨ New Experiences</p>
          <h2>Explore our revolutionary features</h2>
          <p className="section-subtitle">
            Cutting-edge tools that make learning coding fun, social, and immersive
          </p>
        </div>
        <div className="features-grid">
          {NEW_FEATURES.map((feature) => (
            <Link
              key={feature.title}
              to={feature.link}
              className="feature-card"
              style={{ '--feature-color': feature.color }}
              onClick={(e) => {
                if (feature.requiresAuth && !user) {
                  e.preventDefault();
                  setError('Please sign in to access the Metaverse');
                  handleAuthModeChange('signin');
                }
              }}
            >
              <div className="feature-badge">{feature.badge}</div>
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
              <div className="feature-arrow">→</div>
              {feature.requiresAuth && !user && (
                <div className="feature-lock">🔒 Sign in required</div>
              )}
            </Link>
          ))}
        </div>
      </section>

      <section className="brain-breaks">
        <div className="section-header">
          <div>
            <p className="eyebrow">Brain breaks</p>
            <h2>Mini-games keep energy high</h2>
          </div>
          {activeGameConfig && <p className="section-subtitle">{activeGameConfig.blurb}</p>}
        </div>
        <div className="game-tabs">
          {MINI_GAME_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`game-tab ${activeGame === tab.id ? 'active' : ''}`}
              onClick={() => setActiveGame(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="game-surface">
          {activeGame === 'snake' ? <SnakeGame /> : <TicTacToeGame />}
        </div>
      </section>
      </>
      )}

      {showForgotPassword && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowForgotPassword(false);
            setResetEmail('');
            setResetSuccess(false);
            setError('');
          }}
        >
          <div className="modal-content forgot-password-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🔐 Reset Password</h2>
            {resetSuccess ? (
              <div className="reset-success">
                <p className="success-message">
                  ✅ Password reset email sent! Check your inbox and follow the instructions to reset your password.
                </p>
                <button
                  type="button"
                  className="cta primary w-100"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail('');
                    setResetSuccess(false);
                  }}
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <p className="reset-instructions">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                {error && (
                  <div className="auth-alert" role="alert">
                    {error}
                  </div>
                )}
                <div className="form-field">
                  <input
                    type="email"
                    placeholder="Email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="forgot-actions">
                  <button type="submit" className="cta primary" disabled={resetLoading}>
                    {resetLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                  <button
                    type="button"
                    className="cta ghost"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                      setError('');
                    }}
                    disabled={resetLoading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;

